import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  AlertCircle,
  RefreshCw,
  Wallet,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency, cn } from '../../lib/utils';
import { usePrimaryVirtualAccount } from '../../hooks/useCrypto';
import {
  useDefiPools,
  useDefiPortfolio,
  usePoolDeposit,
  DefiPool,
  PoolNetwork,
  PoolProvider,
  RiskLevel,
  getRiskColor,
  getRiskLabel,
  getNetworkColor,
  getProviderLogo,
} from '../../hooks/useDefi';

const NETWORK_OPTIONS = [
  { value: '', label: 'Todas as redes' },
  { value: 'ETHEREUM', label: 'Ethereum' },
  { value: 'BSC', label: 'BNB Smart Chain' },
  { value: 'SOLANA', label: 'Solana' },
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'Todos os providers' },
  { value: 'uniswap', label: 'Uniswap' },
  { value: 'pancakeswap', label: 'PancakeSwap' },
  { value: 'jupiter', label: 'Jupiter' },
  { value: 'raydium', label: 'Raydium' },
];

const RISK_OPTIONS = [
  { value: '', label: 'Todos os riscos' },
  { value: 'LOW', label: 'Baixo Risco' },
  { value: 'MEDIUM', label: 'Risco Moderado' },
  { value: 'HIGH', label: 'Alto Risco' },
];

export function BankingDefiPools() {
  const navigate = useNavigate();
  const { hasCategory } = useAuthStore();
  
  // Get primary virtual account ID from banking API
  const { data: primaryAccount, isLoading: accountLoading } = usePrimaryVirtualAccount();
  const virtualAccountId = primaryAccount?.id || '';

  const [filters, setFilters] = useState<{
    network?: PoolNetwork;
    provider?: PoolProvider;
    riskLevel?: RiskLevel;
  }>({});
  const [search, setSearch] = useState('');
  const [selectedPool, setSelectedPool] = useState<DefiPool | null>(null);
  const [depositAmount, setDepositAmount] = useState('');

  const { data: pools = [], isLoading: poolsLoading } = useDefiPools(filters);
  const { portfolio, positions } = useDefiPortfolio(virtualAccountId);
  const depositMutation = usePoolDeposit(virtualAccountId);
  
  const isLoading = accountLoading || poolsLoading;

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            DeFi não habilitado
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
          </p>
        </Card>
      </div>
    );
  }
  
  // Check if user has a virtual account
  if (!accountLoading && !virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa ter uma conta virtual para acessar funcionalidades DeFi.
          </p>
        </Card>
      </div>
    );
  }

  const filteredPools = pools.filter(pool =>
    pool.name.toLowerCase().includes(search.toLowerCase()) ||
    (pool.tokens && pool.tokens.some(t => t.toLowerCase().includes(search.toLowerCase())))
  );

  const handleDeposit = async () => {
    if (!selectedPool || !depositAmount) return;
    
    try {
      await depositMutation.mutateAsync({
        poolId: selectedPool.id,
        amountBrl: parseFloat(depositAmount),
      });
      setSelectedPool(null);
      setDepositAmount('');
    } catch (error) {
      console.error('Error depositing:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Layers className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Pools DeFi</h1>
            <p className="text-surface-400">Invista e ganhe rendimentos</p>
          </div>
        </div>
        <Button variant="secondary" onClick={() => navigate('/banking/defi/positions')}>
          <Wallet className="w-4 h-4 mr-2" />
          Minhas Posições ({positions.length})
        </Button>
      </div>

      {/* Portfolio Summary */}
      {portfolio.positionsCount > 0 && (
        <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-surface-400 text-sm">Total Investido</p>
              <p className="text-2xl font-bold text-surface-100">
                ${portfolio.totalDeposited.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-surface-400 text-sm">Valor Atual</p>
              <p className="text-2xl font-bold text-surface-100">
                ${portfolio.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-surface-400 text-sm">Rendimentos</p>
              <p className="text-2xl font-bold text-green-400">
                +${portfolio.totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-surface-400 text-sm">Retorno</p>
              <p className={cn(
                'text-2xl font-bold',
                portfolio.totalEarningsPercentage >= 0 ? 'text-green-400' : 'text-red-400'
              )}>
                {portfolio.totalEarningsPercentage >= 0 ? '+' : ''}{portfolio.totalEarningsPercentage.toFixed(2)}%
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Buscar pools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          options={NETWORK_OPTIONS}
          value={filters.network || ''}
          onChange={(e) => setFilters(f => ({ ...f, network: e.target.value as PoolNetwork || undefined }))}
        />
        <Select
          options={PROVIDER_OPTIONS}
          value={filters.provider || ''}
          onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value as PoolProvider || undefined }))}
        />
        <Select
          options={RISK_OPTIONS}
          value={filters.riskLevel || ''}
          onChange={(e) => setFilters(f => ({ ...f, riskLevel: e.target.value as RiskLevel || undefined }))}
        />
      </div>

      {/* Pools Grid */}
      {filteredPools.length === 0 ? (
        <Card className="text-center py-12">
          <Layers className="w-12 h-12 mx-auto mb-4 text-surface-500" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">
            Nenhum pool encontrado
          </h3>
          <p className="text-surface-400">
            Tente ajustar os filtros de busca
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPools.map((pool, index) => (
            <PoolCard
              key={pool.id}
              pool={pool}
              index={index}
              onDeposit={() => setSelectedPool(pool)}
            />
          ))}
        </div>
      )}

      {/* Deposit Modal */}
      <Modal
        isOpen={!!selectedPool}
        onClose={() => {
          setSelectedPool(null);
          setDepositAmount('');
        }}
        title={`Depositar em ${selectedPool?.name}`}
      >
        {selectedPool && (
          <div className="space-y-6">
            {/* Pool Info */}
            <Card className="bg-surface-800/50">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getNetworkColor(selectedPool.network)} flex items-center justify-center text-2xl`}>
                  {getProviderLogo(selectedPool.provider)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-surface-100">{selectedPool.name}</h3>
                  <p className="text-surface-400 text-sm">{selectedPool.provider} • {selectedPool.network}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-surface-400 text-sm">APY</p>
                  <p className="text-xl font-bold text-green-400">{selectedPool.apy}%</p>
                </div>
                <div>
                  <p className="text-surface-400 text-sm">TVL</p>
                  <p className="text-xl font-bold text-surface-100">
                    ${(selectedPool.tvl / 1000000).toFixed(1)}M
                  </p>
                </div>
                <div>
                  <p className="text-surface-400 text-sm">Risco</p>
                  <Badge className={getRiskColor(selectedPool.riskLevel)}>
                    {getRiskLabel(selectedPool.riskLevel)}
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Amount Input */}
            <Input
              label="Valor a depositar (R$)"
              type="number"
              placeholder="0,00"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              min={selectedPool.minDeposit ? selectedPool.minDeposit / 100 : 10}
              step="0.01"
            />

            {/* Estimated Returns */}
            {depositAmount && parseFloat(depositAmount) > 0 && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-surface-400 text-sm">Rendimento estimado (1 ano)</p>
                <p className="text-xl font-bold text-green-400">
                  +{formatCurrency(parseFloat(depositAmount) * (selectedPool.apy / 100))}
                </p>
                <p className="text-xs text-surface-500 mt-1">
                  * Estimativa baseada no APY atual. Rendimentos podem variar.
                </p>
              </div>
            )}

            {/* Risk Warning */}
            {selectedPool.riskLevel === 'HIGH' && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Alto Risco</p>
                    <p className="text-surface-400 text-sm">
                      Este pool possui alto risco. Você pode perder parte ou todo o valor investido.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setSelectedPool(null)}>
                Cancelar
              </Button>
              <Button
                onClick={handleDeposit}
                disabled={!depositAmount || parseFloat(depositAmount) <= 0 || depositMutation.isPending}
              >
                {depositMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Depositando...
                  </>
                ) : (
                  'Confirmar Depósito'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// PoolCard Component
interface PoolCardProps {
  pool: DefiPool;
  index: number;
  onDeposit: () => void;
}

function PoolCard({ pool, index, onDeposit }: PoolCardProps) {
  return (
    <Card 
      className="hover:border-primary-500/50 transition-all duration-300 cursor-pointer group"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getNetworkColor(pool.network)} flex items-center justify-center text-2xl`}>
            {getProviderLogo(pool.provider)}
          </div>
          <div>
            <h3 className="font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">
              {pool.name}
            </h3>
            <p className="text-surface-400 text-sm capitalize">
              {pool.provider} • {pool.network}
            </p>
          </div>
        </div>
        <Badge className={getRiskColor(pool.riskLevel)}>
          {getRiskLabel(pool.riskLevel)}
        </Badge>
      </div>

      {/* Tokens */}
      <div className="flex flex-wrap gap-2 mb-4">
        {pool.tokens && pool.tokens.map((token, i) => (
          <span 
            key={i}
            className="px-2 py-1 rounded-md bg-surface-800 text-surface-300 text-xs font-medium"
          >
            {token}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-surface-500 text-xs mb-1">APY</p>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-xl font-bold text-green-400">{pool.apy}%</span>
          </div>
        </div>
        <div>
          <p className="text-surface-500 text-xs mb-1">TVL</p>
          <p className="text-xl font-bold text-surface-100">
            ${(pool.tvl / 1000000).toFixed(1)}M
          </p>
        </div>
      </div>

      {/* Description */}
      {pool.description && (
        <p className="text-surface-400 text-sm mb-4 line-clamp-2">
          {pool.description}
        </p>
      )}

      {/* Fees */}
      {pool.fees && (
        <div className="flex gap-4 text-xs text-surface-500 mb-4">
          <span>Depósito: {pool.fees.deposit}%</span>
          <span>Saque: {pool.fees.withdrawal}%</span>
          <span>Performance: {pool.fees.performance}%</span>
        </div>
      )}

      {/* Action */}
      <Button 
        className="w-full" 
        onClick={(e) => {
          e.stopPropagation();
          onDeposit();
        }}
      >
        Depositar
      </Button>
    </Card>
  );
}
